import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  TrendingUp, Clock, Activity, AlertTriangle, HeartPulse, CheckCircle2,
  Loader2, RotateCcw, Hospital, XCircle, Timer, Download, ShieldCheck, FileText,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOME_ACTIONS = [
  { key: 'recovering', label: 'Recovering', icon: HeartPulse, cls: 'text-emerald-600 hover:bg-emerald-50 border-emerald-200' },
  { key: 'recovered', label: 'Recovered', icon: CheckCircle2, cls: 'text-cn-primary hover:bg-cn-soft border-cn-primary/30' },
  { key: 'readmitted', label: 'Readmitted', icon: Hospital, cls: 'text-red-600 hover:bg-red-50 border-red-200' },
  { key: 'declined', label: 'Declined care', icon: XCircle, cls: 'text-cn-text-muted hover:bg-cn-soft border-cn-border' },
];

const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return '< 1h';
  if (h < 48) return `${Math.round(h)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const Kpi = ({ icon: Icon, label, value, sub, tone, testId, alert }) => (
  <div className={`cn-card rounded-2xl p-5 ${alert ? 'ring-1 ring-red-200' : ''}`} data-testid={testId}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${tone}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="text-2xl font-display font-extrabold text-cn-text leading-none">{value}</p>
    <p className="text-xs font-semibold text-cn-text mt-1.5">{label}</p>
    {sub && <p className="text-[11px] text-cn-text-muted mt-0.5">{sub}</p>}
  </div>
);

export const PlannerOutcomes = ({ onRecorded }) => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/planner/outcomes`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      toast.error('Could not load outcomes');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const record = async (id, outcome) => {
    setBusyId(id + outcome);
    try {
      await axios.patch(`${API}/planner/referrals/${id}/outcome`, { outcome }, getAuthHeader());
      toast.success('Outcome recorded — thank you for closing the loop');
      await load();
      onRecorded && onRecorded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record outcome');
    } finally { setBusyId(null); }
  };

  const exportReport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/planner/outcomes/report.csv`, {
        ...getAuthHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom-outcomes-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Outcomes report downloaded');
    } catch (e) {
      toast.error('Could not export report');
    } finally { setExporting(false); }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await axios.get(`${API}/planner/outcomes/report.pdf`, {
        ...getAuthHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom-vbc-outcomes-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('VBC report (PDF) downloaded');
    } catch (e) {
      toast.error('Could not export PDF report');
    } finally { setExportingPdf(false); }
  };

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-cn-primary" /></div>;
  }
  if (!data) return null;

  const maxTrend = Math.max(1, ...((data.trend || []).map((t) => t.placements)));
  const o = data.outcomes || {};

  return (
    <div data-testid="planner-outcomes">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="cn-display text-xl text-cn-text">Care outcomes</h2>
          <p className="text-sm text-cn-text-muted">The proof that your referrals lead to good care — and the metric payers and hospitals reward.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={exportReport}
          disabled={exporting}
          className="cn-btn cn-btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="export-outcomes-report"
          title="Download a de-identified outcomes report (CSV) for hospitals & payers"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Export CSV
        </button>
        <button
          onClick={exportPdf}
          disabled={exportingPdf}
          className="cn-btn cn-btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="export-vbc-pdf"
          title="Download a branded, de-identified VBC outcomes report (PDF) for hospitals & payers"
        >
          {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Export VBC report
        </button>
        </div>
      </div>

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5" data-testid="outcomes-kpis">
        <Kpi
          icon={TrendingUp} label="Placement rate" testId="kpi-placement-rate"
          value={`${data.placement_rate ?? 0}%`}
          sub={`${data.placed_count} of ${data.total_referrals} referrals placed`}
          tone="bg-cn-soft text-cn-primary"
        />
        <Kpi
          icon={Timer} label="Median time to placement" testId="kpi-time-to-placement"
          value={fmtHours(data.median_time_to_placement_hours)}
          sub={data.pct_placed_within_24h != null ? `${data.pct_placed_within_24h}% within 24h` : 'No placements yet'}
          tone="bg-blue-100 text-blue-600"
        />
        <Kpi
          icon={Hospital} label="30-day readmission rate" testId="kpi-readmission-rate"
          value={data.readmission_30d_rate != null ? `${data.readmission_30d_rate}%` : '—'}
          sub={`${data.readmissions_30d} readmitted within 30 days`}
          tone={(data.readmission_30d_rate || 0) > 20 ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'}
          alert={(data.readmission_30d_rate || 0) > 20}
        />
        <Kpi
          icon={Activity} label="Active in care" testId="kpi-active-in-care"
          value={data.active_in_care}
          sub="patients currently receiving care"
          tone="bg-amber-100 text-amber-600"
        />
      </div>

      {/* Proactive Risk Monitoring — Risk Engine -> value-based-care proof point */}
      {(() => {
        const rm = data.risk_monitoring;
        if (!rm || rm.monitored === 0) return null;
        return (
          <div className="cn-card rounded-2xl p-5 mb-5 border-l-4 border-l-cn-primary" data-testid="risk-monitoring">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-cn-primary" />
              <p className="text-sm font-bold text-cn-text">Proactive risk monitoring</p>
              <span className="text-[11px] text-cn-text-muted">· {rm.monitored} of your referrals are actively monitored by the Risk Engine</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
              <div className="rounded-xl bg-red-50 dark:bg-red-900/20 p-4" data-testid="rm-at-risk">
                <div className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-4 h-4" /><span className="text-2xl font-display font-extrabold">{rm.patients_at_risk}</span></div>
                <p className="text-xs font-semibold text-cn-text mt-1">Patients at risk now</p>
                <p className="text-[11px] text-cn-text-muted">{rm.high_risk_now} flagged high — clinicians alerted in real time</p>
              </div>
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-4" data-testid="rm-prevented">
                <div className="flex items-center gap-2 text-emerald-600"><ShieldCheck className="w-4 h-4" /><span className="text-2xl font-display font-extrabold">{rm.readmissions_prevented}</span></div>
                <p className="text-xs font-semibold text-cn-text mt-1">Readmissions prevented</p>
                <p className="text-[11px] text-cn-text-muted">Peaked high-risk, recovered, no 30-day readmit</p>
              </div>
              <div className="rounded-xl bg-cn-soft p-4" data-testid="rm-cost-avoided">
                <div className="flex items-center gap-2 text-cn-primary"><TrendingUp className="w-4 h-4" /><span className="text-2xl font-display font-extrabold">${(rm.cost_avoided || 0).toLocaleString()}</span></div>
                <p className="text-xs font-semibold text-cn-text mt-1">Est. readmission cost avoided</p>
                <p className="text-[11px] text-cn-text-muted">at the CMS-cited avg 30-day cost</p>
              </div>
            </div>
            {rm.at_risk_patients?.length > 0 && (
              <div className="mt-4 border-t border-cn-border pt-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-cn-text-muted mb-2">Currently flagged</p>
                <div className="space-y-1.5">
                  {rm.at_risk_patients.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs" data-testid={`rm-flagged-${i}`}>
                      <span className={`w-2 h-2 rounded-full ${p.risk_level === 'high' ? 'bg-red-500' : 'bg-amber-500'}`} />
                      <span className="font-semibold text-cn-text">{p.initials}</span>
                      <span className="text-cn-text-muted">· {p.diagnosis || 'Home care'}</span>
                      {p.top_flag && <span className="text-cn-text-muted truncate hidden sm:inline">· {p.top_flag}</span>}
                      <span className={`ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${p.risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.risk_level}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Outcome breakdown + trend */}
        <div className="lg:col-span-1 space-y-5">
          <div className="cn-card rounded-2xl p-5" data-testid="outcome-breakdown">
            <p className="text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-3">Outcome breakdown</p>
            <div className="space-y-2.5">
              {[
                { k: 'recovering', label: 'Recovering', cls: 'bg-emerald-500' },
                { k: 'recovered', label: 'Recovered', cls: 'bg-cn-primary' },
                { k: 'readmitted', label: 'Readmitted', cls: 'bg-red-500' },
                { k: 'declined', label: 'Declined care', cls: 'bg-cn-text-muted' },
                { k: 'deceased', label: 'Deceased', cls: 'bg-cn-ink-700' },
              ].map((row) => {
                const val = o[row.k] || 0;
                const pct = data.placed_count ? (val / data.placed_count) * 100 : 0;
                return (
                  <div key={row.k} data-testid={`outcome-row-${row.k}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-cn-text font-medium">{row.label}</span>
                      <span className="text-cn-text-muted font-mono">{val}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-cn-soft overflow-hidden">
                      <div className={`h-full ${row.cls} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="cn-card rounded-2xl p-5" data-testid="placement-trend">
            <p className="text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-3">Placements · last 6 months</p>
            <div className="flex items-stretch gap-2 h-28">
              {(data.trend || []).map((t) => (
                <div key={t.month} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`trend-${t.month}`}>
                  <div className="w-full flex-1 flex items-end">
                    <div
                      className="w-full bg-cn-primary/80 rounded-t-md transition-all min-h-[2px]"
                      style={{ height: `${(t.placements / maxTrend) * 100}%` }}
                      title={`${t.placements} placements`}
                    />
                  </div>
                  <span className="text-[10px] text-cn-text-muted">{t.month.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Awaiting outcome — the action list that keeps the loop closed */}
        <div className="lg:col-span-2">
          <div className="cn-card rounded-2xl p-5" data-testid="awaiting-outcome-list">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-cn-text">Awaiting outcome</p>
                {data.awaiting_outcome_count > 0 && (
                  <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                    {data.awaiting_outcome_count}
                  </span>
                )}
              </div>
              <button onClick={load} className="text-xs text-cn-text-muted hover:text-cn-text inline-flex items-center gap-1" data-testid="outcomes-refresh">
                <RotateCcw className="w-3.5 h-3.5" /> Refresh
              </button>
            </div>

            {(!data.awaiting_outcome || data.awaiting_outcome.length === 0) ? (
              <div className="py-10 text-center" data-testid="awaiting-empty">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-cn-text font-medium">All placed patients have a recorded outcome.</p>
                <p className="text-xs text-cn-text-muted mt-1">Great loop hygiene — this is what makes your data credible to payers.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.awaiting_outcome.map((r) => (
                  <div key={r.id} className="rounded-2xl border border-cn-border p-4 bg-cn-bg/40" data-testid={`awaiting-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-semibold text-cn-text text-sm">{r.patient_initials} · {r.primary_diagnosis || r.care_type || 'Home care'}</p>
                        <p className="text-xs text-cn-text-muted mt-0.5 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Placed {r.days_since_placement === 0 ? 'today' : r.days_since_placement === 1 ? '1 day ago' : `${r.days_since_placement} days ago`}
                          {r.days_since_placement >= 14 && (
                            <span className="ml-1 inline-flex items-center gap-0.5 text-amber-700"><AlertTriangle className="w-3 h-3" /> follow up</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2" data-testid={`outcome-actions-${r.id}`}>
                      {OUTCOME_ACTIONS.map((a) => (
                        <button
                          key={a.key}
                          onClick={() => record(r.id, a.key)}
                          disabled={busyId === r.id + a.key}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-white dark:bg-cn-ink-800 transition-colors ${a.cls}`}
                          data-testid={`record-${a.key}-${r.id}`}
                        >
                          {busyId === r.id + a.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <a.icon className="w-3.5 h-3.5" />}
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
